import { z } from 'zod'

import { lenientList, moneySchema, percentSchema } from '@/schemas/common'
import { isoDateSchema } from '@/schemas/portfolio'
import { limitTestSchema, metricReadingSchema } from '@/schemas/reference'

/**
 * The Stock page's tabs — `/api/v5/company/{symbol}/…` — in the app's own
 * vocabulary.
 *
 * Its own file rather than `schemas/company.ts`: that file is the `/companies`
 * directory, snake_case BY CONTRACT (`CompanyTable` reads those keys), and
 * these are the ~15 camelCase shapes of one company's page. Wire shapes live
 * in `@/api/schema` (or, for the three the OpenAPI gets wrong,
 * `features/company/api/wire.ts`) and are never restated here; this file is
 * what the app holds AFTER `profileService`'s mappers have run.
 *
 * Four rules the whole file turns on:
 *
 *  1. NULL IS "NOT REPORTED", NEVER ZERO. Nearly every figure is nullable, and
 *     a screen draws a quiet "—" or "Not reported yet" for it — never a 0 and
 *     never `NotAvailable` (that is for a block with no API at all).
 *  2. FILED FIGURES ARE MOSTLY EMPTY TODAY. Statements, metrics and Advanced
 *     panels come from the external data service, which holds filings for
 *     about 1% of companies and which mm-be does not fall back from. "No rows"
 *     and "every value null" are the NORMAL answer on dev, typed here as such
 *     — an empty `rows`, a panel whose columns all carry `value: null` — and
 *     never an error.
 *  3. DECIMALS ARRIVE AS STRINGS and become a branded `Money` here, once. A
 *     figure the data service passes through as free text that is not a
 *     decimal ("NaN") is mapped to null by the service, not parsed here.
 *  4. VOCABULARY IS CLOSED HERE, NOT ON THE WIRE. Every code-like field is a
 *     bare CharField in mm-be; the service narrows an unknown value to `null`
 *     before these schemas run (`knownWord`), so a new word costs a chip, not
 *     a page.
 */

// ── Requests ─────────────────────────────────────────────────────────────────

/** The Advanced tabs the panel route serves. Any other value is a 404. */
export const PANEL_TABS = ['health', 'earnings', 'value', 'technicals'] as const
export const panelTabSchema = z.enum(PANEL_TABS)

export const QUARTERS = ['q1', 'q2', 'q3', 'q4'] as const
export const quarterSchema = z.enum(QUARTERS)

/** The panel takes `annual` as well; statements and metrics do not (a 400). */
export const PANEL_PERIODS = [...QUARTERS, 'annual'] as const
export const panelPeriodSchema = z.enum(PANEL_PERIODS)

/**
 * A Nepali fiscal year as every company read TAKES it: six digits, `082083`
 * for 2082/83. The Advanced panel ANSWERS with `082-083`; the service
 * normalises that back to this form so a served year can be asked for again.
 * `fiscalYearLabel` (`features/company/lib/period.ts`) prints it.
 */
export const fiscalYearCodeSchema = z
  .string()
  .regex(/^\d{6}$/, 'Expected a fiscal year, e.g. "082083"')

/**
 * One filing period for the metrics: a fiscal year AND a
 * quarter, or no period at all for "the company's latest filing". mm-be
 * refuses one without the other (400 INVALID_PANEL_PERIOD) — a year names
 * four quarters and a quarter names one per year — so the type does too.
 */
export const filingPeriodSchema = z.object({
  year: fiscalYearCodeSchema,
  quarter: quarterSchema,
})

export const metricsQuerySchema = z.object({
  period: filingPeriodSchema.optional(),
})

/** The panel validates the shapes only, so either may be omitted on its own. */
export const panelQuerySchema = z.object({
  year: fiscalYearCodeSchema.optional(),
  period: panelPeriodSchema.optional(),
})

// ── /sentiment/ ──────────────────────────────────────────────────────────────

/**
 * The company's fear-and-greed reading — the same `{score, label}` idea as
 * the market's `MarketMood` (`@/schemas/market`), so `MoodGauge` draws both
 * and the app has one gauge. The model is its own, with its own five bands.
 *
 * `score` is a plain number, clamped 0–100 by the service: it is a coordinate
 * on the track first and a figure second, the rule `marketMoodSchema` sets.
 * `label` is mm-be's verdict CODE — EXTREME_FEAR · FEAR · NEUTRAL · GREED ·
 * EXTREME_GREED — and mm-be leaves the display word to the client.
 */
export const companySentimentSchema = z.object({
  date: isoDateSchema,
  score: z.number().min(0).max(100),
  label: z.string().min(1).nullable(),
})

// ── /metrics/ ────────────────────────────────────────────────────────────────

/**
 * One COMPUTED metric (cost to income, provision coverage, …) — derived
 * upstream from filed figures, so unreachable through `/statement/`.
 * `isProvisional`: the formula moved or a recompute is queued, so the figure
 * may change; `supersededValue` is what the legacy system said.
 */
export const metricValueSchema = z.object({
  code: z.string().min(1),
  label: z.string().min(1),
  /**
   * RATIO · PERCENT · CURRENCY · … — kept open: a new kind is a formatting
   * question, not a reason to drop the metric.
   */
  quantityKind: z.string().min(1).nullable(),
  value: moneySchema.nullable(),
  isProvisional: z.boolean(),
  supersededValue: moneySchema.nullable(),
})

/** Nothing computed is the normal case: empty `metrics`, null `periodId`. */
export const companyMetricsSchema = z.object({
  symbol: z.string().min(1),
  periodId: z.number().int().nullable(),
  metrics: lenientList(metricValueSchema, 'company metric'),
})

// ── /panel/{tab}/ ────────────────────────────────────────────────────────────

/**
 * Why the company's row has the values it has — the upstream's word, kept
 * rather than collapsed into a boolean, because each reads differently:
 *  - AVAILABLE           the period was filed (columns can still be null)
 *  - MISSING_PERIOD      filed before, but not for this period
 *  - NO_ELIGIBLE_PERIOD  never filed at all — the state of ~99% of companies
 * Null: a value this build does not know; draw the columns as they come.
 */
export const PANEL_AVAILABILITIES = ['AVAILABLE', 'MISSING_PERIOD', 'NO_ELIGIBLE_PERIOD'] as const
export const panelAvailabilitySchema = z.enum(PANEL_AVAILABILITIES)

/** Where the company sits against its sector's median — a DIRECTION, not a grade. */
export const SECTOR_VERDICTS = ['ABOVE', 'BELOW', 'ABOUT_THE_SAME'] as const
export const sectorVerdictSchema = z.enum(SECTOR_VERDICTS)

/**
 * The company among its sector for one metric. `members` is how many
 * companies REPORTED this metric, not how many are in the sector — a median
 * over 5 and one over 19 deserve different confidence ("Against 19 banks").
 * The whole object is null when fewer than five reported.
 */
export const panelSectorStatsSchema = z.object({
  median: moneySchema,
  low: moneySchema,
  high: moneySchema,
  members: z.number().int().min(0),
  verdict: sectorVerdictSchema.nullable(),
})

/**
 * One card on an Advanced tab. `value` is null when the company has not
 * reported THIS metric — independently of the panel's `availability`.
 * `unit` is PERCENT · CURRENCY · RATIO · COUNT, or null when mm-be cannot
 * state one safely (it serves net interest margin unitless rather than be
 * wrong by 100x). `reading` and `regulatory` are null for the many metrics
 * nobody has described and no regulation governs.
 */
export const panelColumnSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  unit: z.string().min(1).nullable(),
  value: moneySchema.nullable(),
  sector: panelSectorStatsSchema.nullable(),
  reading: metricReadingSchema.nullable(),
  regulatory: limitTestSchema.nullable(),
})

/**
 * One Advanced tab for one company.
 *
 * `columns` is the feed's DECLARED contract in its served order, so the set
 * of cards is stable and fills in over time — never hardcode a metric list.
 * `year` is the six-digit code (`082083`) and `period` a lowercase quarter or
 * `annual`; both null when the company has no row. `availableTabs` is every
 * tab the company's SECTOR has, in display order — enough to hide a tab
 * before it 404s. `regulatory` repeats every NRB limit in force for the
 * sector, including ones whose metric this feed does not carry, so a card can
 * still draw its reference line. `isSample` is true only on a development
 * server in sample mode: some values are synthetic, and the tab should say so.
 */
export const companyPanelSchema = z.object({
  symbol: z.string().min(1),
  tab: z.string().min(1),
  slug: z.string().min(1),
  /** Bumped upstream whenever the feed's contract widens. */
  revision: z.number().int(),
  year: fiscalYearCodeSchema.nullable(),
  period: z.string().min(1).nullable(),
  availability: panelAvailabilitySchema.nullable(),
  availableTabs: z.array(z.string().min(1)),
  columns: lenientList(panelColumnSchema, 'panel column'),
  regulatory: lenientList(limitTestSchema, 'panel limit'),
  isSample: z.boolean(),
})

/**
 * `404 PANEL_NOT_AVAILABLE_FOR_SECTOR`, as a VALUE: no feed is mapped for this
 * company's sector and tab (Value for every commercial bank, today). Not an
 * error page and not "nothing reported" — the tab says this measure set isn't
 * served for {sector} yet. A plain string beside an object, so the two are
 * told apart with `typeof`, as Broker Chirfaar's `PVRangeMiss` is.
 */
export const PANEL_NOT_AVAILABLE_FOR_SECTOR = 'not-available-for-sector' as const

// ── /financials/{block}/ ─────────────────────────────────────────────────────

/**
 * The Financials tab's blocks, in mm-be's display order (`registry.BLOCKS`).
 * The path spells them with hyphens (`income-statement`); the response with
 * underscores, as here.
 */
export const FINANCIAL_BLOCKS = [
  'essentials',
  'income_statement',
  'balance_sheet',
  'cash_flow',
] as const
export const financialBlockSchema = z.enum(FINANCIAL_BLOCKS)

/** One period a block was read at, and whether the company filed for it. */
export const blockPeriodSchema = z.object({
  year: fiscalYearCodeSchema.nullable(),
  period: z.string().min(1).nullable(),
  availability: panelAvailabilitySchema.nullable(),
})

/**
 * One line of a Financials block: the finance team's column, as their feed
 * declares it (key, label, unit and order are theirs), the same period a year
 * earlier, the change from it, and the sector's median. `previous` and
 * `changePercent` are null when either side is missing or the prior figure is
 * zero; `sector` is null when too few companies reported for a median.
 */
export const blockRowSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  unit: z.string().min(1).nullable(),
  value: moneySchema.nullable(),
  previous: moneySchema.nullable(),
  changePercent: moneySchema.nullable(),
  sector: panelSectorStatsSchema.nullable(),
})

/**
 * One Financials block for one company at one period — the panel's shape, for
 * the Financials tab. `year` and `period` NAME the period the block speaks for
 * (a statement does not), so the tab can carry a period picker. `periods` is
 * the current period, then each earlier year read. `availableBlocks` is every
 * block the company's SECTOR has: several have no cash-flow feed upstream, and
 * the tab draws three blocks there, not an empty fourth.
 */
export const companyFinancialBlockSchema = z.object({
  symbol: z.string().min(1),
  block: financialBlockSchema,
  year: fiscalYearCodeSchema.nullable(),
  period: z.string().min(1).nullable(),
  availability: panelAvailabilitySchema.nullable(),
  availableBlocks: z.array(financialBlockSchema),
  periods: lenientList(blockPeriodSchema, 'block period'),
  rows: lenientList(blockRowSchema, 'financial block row'),
})

// ── /key-numbers/ ────────────────────────────────────────────────────────────

/**
 * A key number mm-be works out from today's price and a filed figure (market
 * cap, P/B, P/E), since no filing carries one. `basis` is mm-be's own sentence
 * for how, and why `value` is null when it is: P/E stays null until the feed
 * carries an annualised EPS. Null `basis`: mm-be sent a blank one.
 */
export const derivedNumberSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  unit: z.string().min(1).nullable(),
  value: moneySchema.nullable(),
  basis: z.string().min(1).nullable(),
})

/**
 * Overview's Key numbers at the company's latest filed period: the finance
 * team's key-numbers feed as a Financials block (`rows`, their columns in
 * their order, each beside its sector median and its year-earlier figure),
 * then the price-derived numbers in mm-be's order. Neither list is a fixed
 * set: a column added upstream is a tile with no release.
 *
 * `NO_ELIGIBLE_PERIOD` with every row value null is the company that has
 * filed nothing yet — a 200, never an error.
 */
export const companyKeyNumbersSchema = z.object({
  symbol: z.string().min(1),
  year: fiscalYearCodeSchema.nullable(),
  period: z.string().min(1).nullable(),
  availability: panelAvailabilitySchema.nullable(),
  periods: lenientList(blockPeriodSchema, 'key-numbers period'),
  rows: lenientList(blockRowSchema, 'key-numbers row'),
  derived: lenientList(derivedNumberSchema, 'derived key number'),
})

// ── /returns/ ────────────────────────────────────────────────────────────────

/** One line the returns read measures: the company, its sector index or NEPSE. */
export const returnSeriesSchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
  lastClose: moneySchema.nullable(),
  lastCloseDate: isoDateSchema.nullable(),
})

/**
 * One look-back window (1W … 3Y), every window ending at the same session.
 * A percent is null when that series has no close near the window's start or
 * end — never 0, which would read as a flat price. The two gaps are the
 * company's figure less the benchmark's, in percentage points.
 */
export const returnWindowSchema = z.object({
  period: z.string().min(1),
  startDate: isoDateSchema,
  company: percentSchema.nullable(),
  sector: percentSchema.nullable(),
  nepse: percentSchema.nullable(),
  vsSector: percentSchema.nullable(),
  vsNepse: percentSchema.nullable(),
})

/** The company's yearly price growth over `years`, compounded; null without a close that far back. */
export const priceCagrSchema = z.object({
  years: z.number().int().positive(),
  startDate: isoDateSchema.nullable(),
  startClose: moneySchema.nullable(),
  percent: percentSchema.nullable(),
})

/**
 * `/company/{symbol}/returns/`: price returns only (no dividends), beside the
 * sector's sub-index and NEPSE, plus price CAGR. `sector` and `nepse` are null
 * when mm-be has no such index row; `asOf` is the session every window ends at.
 */
export const companyReturnsSchema = z.object({
  symbol: z.string().min(1),
  asOf: isoDateSchema.nullable(),
  company: returnSeriesSchema,
  sector: returnSeriesSchema.nullable(),
  nepse: returnSeriesSchema.nullable(),
  windows: lenientList(returnWindowSchema, 'return window'),
  priceCagr: lenientList(priceCagrSchema, 'price CAGR'),
})

// ── /indices/ ────────────────────────────────────────────────────────────────

/**
 * One index the company is in (`/company/{symbol}/indices/`): NEPSE, its sector's sub-index, and
 * whichever of Sensitive, Float and Sensitive Float it is flagged into. `weightPercent` is the
 * company's share of that index's market cap (`market_cap_share_percent`) — the figure a member
 * asks "how much does it move the index" with. `value` and `changePercent` are the index's own
 * latest line, null when mm-be holds no row for it.
 *
 * The index's turnover is left out: a sub-index row carries the market-wide one, and mm-be's own
 * `turnover_share_percent` is the honest figure, which nothing draws.
 */
export const companyIndexSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  value: moneySchema.nullable(),
  changePercent: percentSchema.nullable(),
  weightPercent: percentSchema.nullable(),
})

/**
 * `/company/{symbol}/indices/`. `session` is the day every line is from. A company in no index
 * (a fund, a debenture) is a valid answer with an empty `indices`, and one row that fails to
 * parse costs that row, not the card.
 */
export const companyIndicesSchema = z.object({
  symbol: z.string().min(1),
  session: isoDateSchema.nullable(),
  indices: lenientList(companyIndexSchema, 'company index'),
})

// ── /volatility/ ─────────────────────────────────────────────────────────────

/**
 * `/company/{symbol}/volatility/`: the standard deviation of daily % moves over the last
 * `windowSessions` sessions, for the company and for NEPSE over the same sessions, and their ratio.
 * Every measured field is null under 20 sessions of history, and `ratio` alone is null against a
 * flat NEPSE. `label` is mm-be's own sentence for the ratio ("about as volatile as NEPSE"), kept
 * as served so this page and any summary say the same thing.
 */
export const companyVolatilitySchema = z.object({
  symbol: z.string().min(1),
  windowSessions: z.number().int().nonnegative(),
  sessionsUsed: z.number().int().nonnegative(),
  dailyPercent: percentSchema.nullable(),
  nepsePercent: percentSchema.nullable(),
  ratio: moneySchema.nullable(),
  label: z.string().min(1).nullable(),
})

// ── /technicals/ ─────────────────────────────────────────────────────────────

/** MACD against its signal line. mm-be's NO_CALCULATION arrives here as null. */
export const MACD_SIGNALS = ['UP', 'DOWN', 'NEUTRAL'] as const
export const macdSignalSchema = z.enum(MACD_SIGNALS)

/** The Bollinger signal. mm-be's NO_CALCULATION arrives here as null. */
export const BOLLINGER_SIGNALS = ['BUY', 'SELL'] as const
export const bollingerSignalSchema = z.enum(BOLLINGER_SIGNALS)

/**
 * The precomputed technical indicators — mm-be's legacy cron writes one row a
 * session, so these are the last close's figures. Each is null when the
 * cron could not compute it (too short a history).
 *
 * `thirtyDayAverage` is the legacy row's `thirty_day_average` column, which
 * mm-be does not document as a price or a volume average — confirm against a
 * live row before labelling it. The overview's `quote.thirtyDayAverage` IS
 * documented: the mean close of the last 30 sessions.
 */
export const companyTechnicalsSchema = z.object({
  /** The session these figures were computed on. */
  asOf: isoDateSchema,
  rsi: moneySchema.nullable(),
  macd: moneySchema.nullable(),
  macdSignal: moneySchema.nullable(),
  macdVsSignal: macdSignalSchema.nullable(),
  stochastic: moneySchema.nullable(),
  adx: moneySchema.nullable(),
  mfi: moneySchema.nullable(),
  sma20: moneySchema.nullable(),
  sma50: moneySchema.nullable(),
  sma200: moneySchema.nullable(),
  ema8: moneySchema.nullable(),
  ema13: moneySchema.nullable(),
  ema20: moneySchema.nullable(),
  bollingerUpper: moneySchema.nullable(),
  bollingerMiddle: moneySchema.nullable(),
  bollingerLower: moneySchema.nullable(),
  bollingerSignal: bollingerSignalSchema.nullable(),
  vwap13: moneySchema.nullable(),
  thirtyDayAverage: moneySchema.nullable(),
  weekBeta: moneySchema.nullable(),
  monthBeta: moneySchema.nullable(),
  quarterBeta: moneySchema.nullable(),
  yearBeta: moneySchema.nullable(),
  /** Kitta traded in the latest session. Not `.int()`: a server float must not fail the row. */
  volumeToday: z.number().nullable(),
  /** The mean kitta of the 30 sessions before it; null under 30 sessions on record. */
  volumeThirtyDayAverage: moneySchema.nullable(),
  /** mm-be's own division of the two: "1.85" is 1.85 times a normal day. */
  volumeVsAverage: moneySchema.nullable(),
})

// ── /dividends/ ──────────────────────────────────────────────────────────────

/**
 * One dividend. Cash and bonus are PERCENTS OF FACE VALUE (Rs 100), not
 * rupees. A null `bonusPercent` is a cash-only dividend; `totalPercent` is
 * cash + bonus and is always set (mm-be fixed legacy's null total).
 * `fiscalYear` is free text an editor typed — "2082/83", "082-083" both occur.
 */
export const dividendSchema = z.object({
  fiscalYear: z.string().min(1).nullable(),
  cashPercent: percentSchema,
  bonusPercent: percentSchema.nullable(),
  totalPercent: percentSchema,
  announcedDate: isoDateSchema.nullable(),
  bookClosureDate: isoDateSchema.nullable(),
  distributionDate: isoDateSchema.nullable(),
  bonusListingDate: isoDateSchema.nullable(),
})

/**
 * The stat line above the history, computed by mm-be from the same rows.
 *  - `nextBookClosureDate` / `daysToBookClosure`: the soonest cut-off still
 *    ahead (Nepal time); both null when none is.
 *  - `windowYears`: the record's window — six fiscal years, or fewer for a
 *    younger company (a three-year-old company is "3 of 3", never "3 of 6").
 *  - `yearsPaid`: years inside that window that paid anything.
 *  - `averageTotalPercent`: averaged over the years that PAID, not the window.
 * The window fields are null together when no row's fiscal year is readable.
 */
export const dividendSummarySchema = z.object({
  nextBookClosureDate: isoDateSchema.nullable(),
  daysToBookClosure: z.number().int().nullable(),
  windowYears: z.number().int().min(0).nullable(),
  yearsPaid: z.number().int().min(0).nullable(),
  averageTotalPercent: percentSchema.nullable(),
})

/**
 * The history, newest first, and its stat line. `summary` is null only when
 * the response's `meta.summary` was missing or unreadable — it is not in the
 * OpenAPI (`meta` is free-form), so nothing but the parse vouches for it.
 */
export const companyDividendsSchema = z.object({
  rows: lenientList(dividendSchema, 'dividend'),
  summary: dividendSummarySchema.nullable(),
})

// ── /events/ ─────────────────────────────────────────────────────────────────

/** What kind of corporate action an event is. Cash and bonus are separate events. */
export const EVENT_TYPES = ['DIVIDEND', 'BONUS', 'AGM', 'RIGHT_SHARE', 'AUCTION'] as const
export const eventTypeSchema = z.enum(EVENT_TYPES)

/**
 * Relative to today in Nepal time: `upcoming` is ahead, `active` is within the
 * last seven days, `completed` is older.
 */
export const EVENT_STATUSES = ['upcoming', 'active', 'completed'] as const
export const eventStatusSchema = z.enum(EVENT_STATUSES)

export const companyEventSchema = z.object({
  date: isoDateSchema,
  type: eventTypeSchema.nullable(),
  title: z.string().min(1),
  status: eventStatusSchema.nullable(),
  /** An AGM's venue and agenda on one line. */
  subtitle: z.string().min(1).nullable(),
})

/**
 * The corporate-action calendar, composed by mm-be from dividends, AGMs,
 * right shares and auctions. `upcoming` holds both `upcoming` AND `active`
 * events, soonest first; `past` holds `completed` ones, newest first, capped
 * at 50. Ex-dividend dates, expected results and share splits have no source
 * and never appear.
 */
export const companyEventsSchema = z.object({
  upcoming: lenientList(companyEventSchema, 'upcoming event'),
  past: lenientList(companyEventSchema, 'past event'),
})

// ── Paged lists (/agms/, /announcements/) ────────────────────────────────────

/**
 * One server page. `total` and `totalPages` count the WHOLE list (mm-be counts
 * before it slices), which is what "Showing 6 of 18 meetings" needs.
 */
const companyPageSchema = <T>(rowSchema: z.ZodType<T>, label: string) =>
  z.object({
    rows: lenientList(rowSchema, label),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
    totalPages: z.number().int().min(0),
  })

/** UPCOMING / HELD from the meeting date in Nepal time; null when undated. */
export const AGM_STATUSES = ['UPCOMING', 'HELD'] as const
export const agmStatusSchema = z.enum(AGM_STATUSES)

export const agmSchema = z.object({
  /** Free text an editor typed: "26th AGM". */
  label: z.string().min(1).nullable(),
  date: isoDateSchema.nullable(),
  bookClosureDate: isoDateSchema.nullable(),
  venue: z.string().min(1).nullable(),
  /** Plain text. */
  agenda: z.string().min(1).nullable(),
  /**
   * Wall-clock start in Nepal time, "11:00:00"; null when the notice gave none.
   * Defaulted so an older row without the key does not fail the whole AGM list.
   */
  time: z.string().min(1).nullable().default(null),
  status: agmStatusSchema.nullable(),
})

export const agmPageSchema = companyPageSchema(agmSchema, 'AGM')

/**
 * One dated notice — the company's and NEPSE's, NOT press coverage. `type`
 * is free text (display it sentence case). `message` is the one-paragraph
 * text; `description` is what "Read notice" expands into, when there is one.
 */
export const announcementSchema = z.object({
  date: isoDateSchema,
  title: z.string().min(1),
  type: z.string().min(1).nullable(),
  message: z.string().min(1).nullable(),
  description: z.string().min(1).nullable(),
})

export const announcementPageSchema = companyPageSchema(announcementSchema, 'announcement')

// ── /shareholding/ and /board-of-directors/ ──────────────────────────────────

/** A major promoter. `ownership` is free text as filed ("51%"), not a number. */
export const promoterSchema = z.object({
  name: z.string().min(1),
  designation: z.string().min(1).nullable(),
  ownership: z.string().min(1).nullable(),
})

/**
 * A major shareholder. `holdingValue` is units × the latest price, null when
 * there is no price (never 0); `percentage` is of the listed shares.
 */
export const shareholderSchema = z.object({
  name: z.string().min(1),
  units: z.number().int().min(0),
  holdingValue: moneySchema.nullable(),
  percentage: percentSchema,
})

/**
 * The promoter/public split and the two named lists. The split is from
 * mm-be's `company_profile` and can disagree with the overview profile's own
 * holding columns — draw the Share holding tab from THIS one.
 */
export const companyShareholdingSchema = z.object({
  promoterHolding: percentSchema.nullable(),
  publicHolding: percentSchema.nullable(),
  promoters: lenientList(promoterSchema, 'promoter'),
  shareholders: lenientList(shareholderSchema, 'shareholder'),
})

/**
 * One director, in mm-be's order: the chair first, then directors, then the
 * company secretary. `isChair` is read from the designation's words.
 */
export const boardMemberSchema = z.object({
  name: z.string().min(1),
  designation: z.string().min(1).nullable(),
  ownership: z.string().min(1).nullable(),
  isChair: z.boolean(),
})

export const boardOfDirectorsSchema = lenientList(boardMemberSchema, 'board member')

// ── /swot/ ───────────────────────────────────────────────────────────────────

/** The analyst notes, split per box. An unpopulated box is `[]`. */
export const companySwotSchema = z.object({
  strengths: z.array(z.string().min(1)),
  weaknesses: z.array(z.string().min(1)),
  opportunities: z.array(z.string().min(1)),
  threats: z.array(z.string().min(1)),
})

// ── /annual-reports/ ─────────────────────────────────────────────────────────

export const REPORT_TYPES = ['annual', 'quarterly'] as const
export const reportTypeSchema = z.enum(REPORT_TYPES)

/**
 * One report link: annual ones first, then quarterly, each newest first.
 * `year` is the Nepali fiscal year as mm-be stores it ("2080/81"); `link` is
 * the PDF's URL — open it in a new tab, it is a third-party file.
 */
export const reportLinkSchema = z.object({
  name: z.string().min(1),
  year: z.string().min(1).nullable(),
  link: z.string().min(1),
  type: reportTypeSchema.nullable(),
})

export const annualReportsSchema = lenientList(reportLinkSchema, 'report')

// ── /company/compare/ ────────────────────────────────────────────────────────

/**
 * One company's comparison figures. Market facts (price, move, market cap,
 * 52-week range, dividend yield) are mm-be's own; EPS, book value, ROE and NPL
 * are the data service's KEY_RATIOS filing, so they are mostly null today. P/E
 * and P/B are price ÷ the filed EPS / book value, null when either is.
 */
export const compareCompanySchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
  sectorName: z.string().min(1).nullable(),
  lastPrice: moneySchema.nullable(),
  changePercent: percentSchema.nullable(),
  marketCap: moneySchema.nullable(),
  fiftyTwoWeekHigh: moneySchema.nullable(),
  fiftyTwoWeekLow: moneySchema.nullable(),
  peRatio: moneySchema.nullable(),
  pbRatio: moneySchema.nullable(),
  eps: moneySchema.nullable(),
  bookValue: moneySchema.nullable(),
  dividendYield: percentSchema.nullable(),
  roe: percentSchema.nullable(),
  npl: percentSchema.nullable(),
})

/** Which direction wins a metric. */
export const LEAD_DIRECTIONS = ['higher', 'lower'] as const
export const leadDirectionSchema = z.enum(LEAD_DIRECTIONS)

/** The `CompareCompany` figures mm-be ranks in `leads`. */
export const COMPARE_LEAD_FIELDS = [
  'changePercent',
  'marketCap',
  'peRatio',
  'pbRatio',
  'eps',
  'bookValue',
  'dividendYield',
  'roe',
  'npl',
] as const
export const compareLeadFieldSchema = z.enum(COMPARE_LEAD_FIELDS)

/**
 * "Where each leads": the symbol with the single best value for one metric.
 *
 * `metric` is mm-be's snake_case code (`pe_ratio`) — the SAME code
 * `/market/sectors/{s}/stats/` keys its medians by, so it is the join to the
 * Sector column. `field` is the `CompareCompany` key that code ranks
 * (`peRatio`), or null for a metric this build does not know. `leader` is
 * null on a tie or with fewer than two values — highlight nothing then.
 */
export const metricLeadSchema = z.object({
  metric: z.string().min(1),
  field: compareLeadFieldSchema.nullable(),
  label: z.string().min(1),
  better: leadDirectionSchema.nullable(),
  leader: z.string().min(1).nullable(),
})

/**
 * PROPOSED (`asks/cp1.md` A8) — one row of Compare's Financials tab: a filed figure ("Net profit,
 * FY 2081–82", deposits, loans, total assets) for each company, by symbol; null where one has not
 * filed it. Read only once `COMPARE_FINANCIALS_AVAILABLE` flips.
 */
export const compareFinancialRowSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  values: z.record(z.string(), moneySchema.nullable()),
})

/** `companies` is in the order asked — the column order. */
export const companyComparisonSchema = z.object({
  companies: lenientList(compareCompanySchema, 'compared company'),
  leads: lenientList(metricLeadSchema, 'metric lead'),
  financials: lenientList(compareFinancialRowSchema, 'compared financial').default([]),
})

export type PanelTab = z.infer<typeof panelTabSchema>
export type Quarter = z.infer<typeof quarterSchema>
export type PanelPeriod = z.infer<typeof panelPeriodSchema>
export type FilingPeriod = z.infer<typeof filingPeriodSchema>
export type MetricsQuery = z.infer<typeof metricsQuerySchema>
export type PanelQuery = z.infer<typeof panelQuerySchema>
export type CompanySentiment = z.infer<typeof companySentimentSchema>
export type MetricValue = z.infer<typeof metricValueSchema>
export type CompanyMetrics = z.infer<typeof companyMetricsSchema>
export type PanelAvailability = z.infer<typeof panelAvailabilitySchema>
export type SectorVerdict = z.infer<typeof sectorVerdictSchema>
export type PanelSectorStats = z.infer<typeof panelSectorStatsSchema>
export type PanelColumn = z.infer<typeof panelColumnSchema>
export type CompanyPanel = z.infer<typeof companyPanelSchema>
export type PanelNotAvailable = typeof PANEL_NOT_AVAILABLE_FOR_SECTOR
export type FinancialBlock = z.infer<typeof financialBlockSchema>
export type BlockPeriod = z.infer<typeof blockPeriodSchema>
export type BlockRow = z.infer<typeof blockRowSchema>
export type CompanyFinancialBlock = z.infer<typeof companyFinancialBlockSchema>
export type DerivedNumber = z.infer<typeof derivedNumberSchema>
export type CompanyKeyNumbers = z.infer<typeof companyKeyNumbersSchema>
export type ReturnSeries = z.infer<typeof returnSeriesSchema>
export type ReturnWindow = z.infer<typeof returnWindowSchema>
export type PriceCagr = z.infer<typeof priceCagrSchema>
export type CompanyReturns = z.infer<typeof companyReturnsSchema>
export type MacdSignal = z.infer<typeof macdSignalSchema>
export type BollingerSignal = z.infer<typeof bollingerSignalSchema>
export type CompanyTechnicals = z.infer<typeof companyTechnicalsSchema>
export type CompanyIndex = z.infer<typeof companyIndexSchema>
export type CompanyIndices = z.infer<typeof companyIndicesSchema>
export type CompanyVolatility = z.infer<typeof companyVolatilitySchema>
export type Dividend = z.infer<typeof dividendSchema>
export type DividendSummary = z.infer<typeof dividendSummarySchema>
export type CompanyDividends = z.infer<typeof companyDividendsSchema>
export type EventType = z.infer<typeof eventTypeSchema>
export type EventStatus = z.infer<typeof eventStatusSchema>
export type CompanyEvent = z.infer<typeof companyEventSchema>
export type CompanyEvents = z.infer<typeof companyEventsSchema>
export type AgmStatus = z.infer<typeof agmStatusSchema>
export type Agm = z.infer<typeof agmSchema>
export type AgmPage = z.infer<typeof agmPageSchema>
export type Announcement = z.infer<typeof announcementSchema>
export type AnnouncementPage = z.infer<typeof announcementPageSchema>
export type Promoter = z.infer<typeof promoterSchema>
export type Shareholder = z.infer<typeof shareholderSchema>
export type CompanyShareholding = z.infer<typeof companyShareholdingSchema>
export type BoardMember = z.infer<typeof boardMemberSchema>
export type CompanySwot = z.infer<typeof companySwotSchema>
export type ReportType = z.infer<typeof reportTypeSchema>
export type ReportLink = z.infer<typeof reportLinkSchema>
export type CompareCompany = z.infer<typeof compareCompanySchema>
export type LeadDirection = z.infer<typeof leadDirectionSchema>
export type CompareLeadField = z.infer<typeof compareLeadFieldSchema>
export type MetricLead = z.infer<typeof metricLeadSchema>
export type CompareFinancialRow = z.infer<typeof compareFinancialRowSchema>
export type CompanyComparison = z.infer<typeof companyComparisonSchema>
